// Corrigir um contato já registrado.
//
// O registro de contato é rápido de propósito — nome digitado na rua, telefone
// no meio de uma ligação — e por isso sai errado com frequência. Sem correção,
// o erro fica: a linha do tempo é só-inserção e o nome errado ainda vaza para
// o cadastro da empresa (o "Registrar Contato" grava `contact`/`phone` nela).
//
// O que se corrige aqui é o que se DIGITA: nome, telefones, canal, observação e
// próximo passo. O que é PROVA não se toca — anexo, data/hora do contato, dia
// que contou para a meta e desfecho continuam como foram registrados. Editar
// isso não seria correção, seria reescrever o que aconteceu.

import { exigeComprovante } from "./abordagem";

export const CAMPOS_EDITAVEIS = [
  ["contactName", "Nome do contato"],
  ["phoneCelular", "Celular / WhatsApp"],
  ["phoneFixo", "Telefone fixo"],
  ["spokeWith", "Falou com"],
  ["note", "Observação"],
  ["nextAction", "Próximo passo"],
  ["nextActionDate", "Data do próximo passo"],
  ["channel", "Canal"],
];

const ROTULO = Object.fromEntries(CAMPOS_EDITAVEIS);

const limpo = (v) => (typeof v === "string" ? v.trim() : v ?? "");

/**
 * O que mudou de fato, campo a campo. Patch que não muda nada não vira edição:
 * abrir e fechar o formulário não deve sujar a trilha.
 */
export function diferencas(interacao = {}, patch = {}) {
  const out = [];
  for (const [campo] of CAMPOS_EDITAVEIS) {
    if (!(campo in patch)) continue;
    const de = limpo(interacao[campo]);
    const para = limpo(patch[campo]);
    if (de !== para) out.push({ campo, rotulo: ROTULO[campo], de, para });
  }
  return out;
}

/**
 * Canal que exige print não pode ser assumido por um contato que não tem print.
 * Sem esta trava, bastaria registrar como "Visita Presencial" (que aceita o
 * "por que não há comprovante") e depois trocar para WhatsApp — o contato
 * ficaria contando como conversa provada sem prova nenhuma.
 */
export function erroDeCanal(interacao = {}, patch = {}, ehParceiro = false) {
  if (!ehParceiro || !("channel" in patch)) return "";
  const novo = patch.channel;
  if (novo === interacao.channel) return "";
  if (exigeComprovante(novo) && !interacao.comprovantePath) {
    return `Contato sem anexo não pode virar "${novo}" — esse canal exige comprovante. Registre um contato novo com o print.`;
  }
  return "";
}

/**
 * Aplica a correção na interação e devolve a nova. A edição fica DENTRO do
 * próprio contato (`edicoes`), como manda a trilha — coluna nova para isso não
 * existe, e o que não tem coluna some na primeira recarga.
 */
export function aplicarNoContato(interacao, patch, autor = "") {
  const mudou = diferencas(interacao, patch);
  if (!mudou.length) return { interacao, mudou };
  const campos = Object.fromEntries(mudou.map((m) => [m.campo, m.para]));
  return {
    mudou,
    interacao: {
      ...interacao,
      ...campos,
      editadoEm: new Date().toISOString(),
      editadoPor: autor || interacao.editadoPor || "",
      edicoes: [
        ...(Array.isArray(interacao.edicoes) ? interacao.edicoes : []),
        { ts: new Date().toISOString(), autor: autor || "", campos: mudou },
      ],
    },
  };
}

/**
 * Corrige o contato dentro da empresa (cliente ou parceiro). `ref` é
 * `{ id }` ou, para registro antigo sem id, `{ idx }`.
 *
 * O nome e o telefone que o contato gravou na EMPRESA também se corrigem — mas
 * só quando ela ainda carrega exatamente o valor errado. Se alguém já arrumou o
 * cadastro à mão, a correção do contato antigo não passa por cima disso.
 */
export function corrigirContato(entidade, ref, patch, autor = "") {
  const lista = Array.isArray(entidade?.interactions) ? entidade.interactions : [];
  // Contato antigo pode não ter id (o campo é recente): aí a identidade é a
  // posição na lista. Sem isso, justamente os registros mais velhos — os que
  // mais acumularam erro de digitação — ficariam sem conserto.
  const casa = (i, k) => (ref?.id ? i?.id === ref.id : !i?.id && k === ref?.idx);
  const alvo = lista.find(casa);
  if (!alvo) return entidade;

  const { interacao, mudou } = aplicarNoContato(alvo, patch, autor);
  if (!mudou.length) return entidade;

  const proximo = {
    ...entidade,
    interactions: lista.map((i, k) => (casa(i, k) ? interacao : i)),
  };

  const trocou = (campo) => mudou.find((m) => m.campo === campo);
  const nome = trocou("contactName");
  if (nome && nome.para && limpo(entidade.contact) === nome.de) proximo.contact = nome.para;

  const cel = trocou("phoneCelular");
  if (cel && cel.para) {
    if (limpo(entidade.phone) === cel.de) proximo.phone = cel.para;
    if (limpo(entidade.whatsapp) === cel.de) proximo.whatsapp = cel.para;
  }

  return proximo;
}
